import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';
import { Input } from './Input';
import { RichTextEditor } from './RichTextEditor';
interface AddCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (customer: {
    customerId: string;
    name: string;
    address: string;
    phone: string;
    email?: string;
    notes: string;
    secondaryContactName?: string;
    secondaryContactPhone?: string;
  }) => void;
  nextCustomerId: string;
}
export function AddCustomerModal({
  isOpen,
  onClose,
  onAdd,
  nextCustomerId
}: AddCustomerModalProps) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [secondaryContactName, setSecondaryContactName] = useState('');
  const [secondaryContactPhone, setSecondaryContactPhone] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onAdd({
      customerId: nextCustomerId,
      name,
      address,
      phone,
      email: email || undefined,
      notes,
      secondaryContactName: secondaryContactName || undefined,
      secondaryContactPhone: secondaryContactPhone || undefined
    });
    setName('');
    setAddress('');
    setPhone('');
    setEmail('');
    setNotes('');
    setSecondaryContactName('');
    setSecondaryContactPhone('');
    onClose();
  };
  return <AnimatePresence data-id="element-29" data-mp="369rf4c1tfx9w">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} data-id="element-30" data-mp="2rdjl6k0lvhhf" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        duration: 0.3
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" data-id="element-31" data-mp="1l02euvj4mmpa">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 my-8" data-id="element-32" data-mp="2zv2a4zw03zjg">
              <div className="flex items-center justify-between mb-6" data-id="element-33" data-mp="2uw91bkrnswpm">
                <div data-id="element-34" data-mp="2uybyqpv55s4d">
                  <h2 className="text-2xl font-semibold text-stone-900" data-id="element-35" data-mp="2pxnxzch41wwy">
                    Tilføj kunde
                  </h2>
                  <p className="text-sm text-stone-500 mt-1" data-id="element-36" data-mp="t42xui0vz5mi">
                    Kunde-ID:{' '}
                    <span className="font-medium text-teal-600" data-id="element-37" data-mp="o72rs741e2ve">
                      {nextCustomerId}
                    </span>
                  </p>
                </div>
                <button onClick={onClose} className="text-stone-400 hover:text-stone-600 transition-colors" aria-label="Luk" data-id="element-38" data-mp="10odf66v43ap2">
                  <XIcon className="w-6 h-6" data-id="element-39" data-mp="1ae6t3eiq81wh" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-40" data-mp="cqpr7hne95ej">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-41" data-mp="csizf1dnnxon">
                  <Input label="Kundenavn" value={name} onChange={e => setName(e.target.value)} placeholder="Indtast kundenavn" required data-id="element-42" data-mp="hqkdt904z8ts" />

                  <Input label="Telefon" value={phone} onChange={e => setPhone(e.target.value)} placeholder="f.eks. +45 12 34 56 78" type="tel" data-id="element-43" data-mp="1y4avjik6hvlw" />
                </div>

                <Input label="Adresse" value={address} onChange={e => setAddress(e.target.value)} placeholder="Indtast adresse" data-id="element-44" data-mp="1wmvfzm1r69ja" />

                <Input label="Email (valgfri)" value={email} onChange={e => setEmail(e.target.value)} placeholder="kunde@email.dk" type="email" data-id="element-45" data-mp="25n0ltrraszvc" />

                <div className="pt-4 border-t border-stone-100" data-id="element-46" data-mp="2fjn72hnjdsxw">
                  <h3 className="text-sm font-medium text-stone-900 mb-3" data-id="element-47" data-mp="2tc32oxe3yoos">
                    Ekstra kontaktperson (valgfri)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-48" data-mp="2oge16oij2xpu">
                    <Input label="Navn" value={secondaryContactName} onChange={e => setSecondaryContactName(e.target.value)} placeholder="Navn på kontaktperson" data-id="element-49" data-mp="33bk7lcljcyjp" />
                    <Input label="Telefon" value={secondaryContactPhone} onChange={e => setSecondaryContactPhone(e.target.value)} placeholder="Telefonnummer" type="tel" data-id="element-50" data-mp="3lykzjdee6gc4" />
                  </div>
                </div>

                <RichTextEditor label="Noter" value={notes} onChange={setNotes} placeholder="Tilføj noter om denne kunde" rows={3} data-id="element-51" data-mp="2y873lbsorxc9" />

                <div className="flex gap-3 pt-2" data-id="element-52" data-mp="2u7naqkf5bfvx">
                  <Button type="button" variant="secondary" onClick={onClose} className="flex-1" data-id="element-53" data-mp="2u6c143z0rdww">
                    Annuller
                  </Button>
                  <Button type="submit" variant="primary" className="flex-1" data-id="element-54" data-mp="216p4bcyk04xg">
                    Tilføj kunde
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}